import { queryOptions } from '@tanstack/react-query';

import { electronApis } from '@/features/electron-api';
import { AppCapability, isCapable, isMac } from '@/utils/capabilities';

export const isAccessibilityPermissionsGrantedQueryOptions = () =>
  queryOptions({
    queryKey: ['is-accessibility-permissions-granted'],
    retry: true,
    retryDelay: 1000,
    queryFn: async () => {
      if (!isCapable(AppCapability.ContextCapture))
        throw new Error('Accessbility permission is not available in browser');

      const isGranted =
        await electronApis.context.checkAccessibilityPermissions();

      if (!isGranted)
        throw new Error('Oops! Accessbility permission is not granted');

      return isGranted;
    },
  });

export const checkAccessibilityPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: ['check-accessibility-permission'],
    queryFn: () => electronApis.context.checkAccessibilityPermissions(),
    retry: false,
  });

// Poll only while missing: returning from System Settings often doesn't
// refire focus events in Electron, so focus refetch alone is unreliable.
const refetchWhileMissing = (query: {
  state: { data: boolean | undefined };
}) => (query.state.data === false ? 3000 : false);

/** Accessibility status that follows a grant made in System Settings */
export const watchAccessibilityPermissionQueryOptions = () =>
  queryOptions({
    ...checkAccessibilityPermissionsQueryOptions(),
    enabled: isMac(),
    refetchOnWindowFocus: true,
    refetchInterval: refetchWhileMissing,
  });

export const screenRecordingPermissionQueryOptions = () =>
  queryOptions({
    queryKey: ['check-screen-recording-permission'],
    queryFn: () => electronApis.context.checkScreenRecordingPermission(),
    enabled: isMac(),
    refetchOnWindowFocus: true,
    refetchInterval: refetchWhileMissing,
    retry: false,
  });

export const FULL_DISK_ACCESS_PERMISSION_QUERY_KEY = [
  'check-full-disk-access-permission',
] as const;

export const fullDiskAccessPermissionQueryOptions = ({
  enabled = true,
}: { enabled?: boolean } = {}) =>
  queryOptions({
    queryKey: FULL_DISK_ACCESS_PERMISSION_QUERY_KEY,
    queryFn: () => electronApis.system.checkFullDiskAccessPermission(),
    enabled: isMac() && enabled,
    refetchOnWindowFocus: true,
    refetchInterval: refetchWhileMissing,
    retry: false,
  });
